import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReactiveFormsModule } from '@angular/forms';
import { first } from 'rxjs';
import { QuestionReasonEnum } from 'src/app/models/shared-models';
import { FeatureFlags } from 'src/app/services/feature-flag.service';
import { SessionService } from 'src/app/services/session.service';
import { environment } from 'src/environments/environment';
import { BaseWizardComponent } from '../base-wizard.component';
import { IfFeatureDisabledDirective } from 'src/app/core/directives/feature.directive';
import {
  IAcknowledgementsDataModel, IActivityStorageDataModel, ICategoryPurposeActivityDataModel, ICategoryPurposeDataModel, IChannelDimensionsModel, IExchangeAreaDataModel, ILandholderConfirmationModel,
  ILandholderDetailsModel, ILotPlanModel, INotificationAreaModel, INotifierDetailsModel, IPurposeDataModel, IReasonModel, IReferenceDataModel, IRegionalEcosystem, IRegionalEcosystemDataModel, IRegionalEcosystemStatusModel
} from '../models/veg-wizard-models';
import { ReferenceDataService } from '../services/reference-data-service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';
import { IfFeatureEnabledDirective } from 'src/app/core/directives/feature.directive';

@UntilDestroy()
@Component({
  selector: 'app-review-notification',
  templateUrl: './review-notification.component.html',
  styleUrls: ['./review-notification.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IfFeatureDisabledDirective,
    IfFeatureEnabledDirective
  ],
})
export class ReviewNotificationComponent extends BaseWizardComponent implements OnInit {

  landholderDetails: ILandholderDetailsModel | undefined;
  landholderConfirmationDetails: ILandholderConfirmationModel | undefined;
  notifierDetails: INotifierDetailsModel | undefined;
  notificationAreaDetails: INotificationAreaModel | undefined;
  lotOnPlanDetails: ILotPlanModel | undefined;
  categoryDetails: ICategoryPurposeDataModel[] = [];
  categoryDisplayValue = '';
  purposeDetails: IPurposeDataModel | undefined;
  regionalEcosystems: IRegionalEcosystemDataModel | undefined;
  regionalEcosystemStatuses: IRegionalEcosystemStatusModel | undefined;
  activitiesDetails: IActivityStorageDataModel | undefined;
  isCoastalResponse: boolean | undefined;
  acknowledgementsDetails: IAcknowledgementsDataModel | undefined;
  acknowledgements: string[] = [];
  reasonText: string[] | undefined;
  reasons: IReasonModel[] | undefined;
  exchangeAreaData: IExchangeAreaDataModel | undefined;
  channelDimensionsDetails: IChannelDimensionsModel | undefined;
  states: IReferenceDataModel[] = [];
  regionalEcosystemsToDisplay: IRegionalEcosystem[] | undefined;

  constructor(
    private vwds: VegWizardDataService,
    private sessionService: SessionService,
    private referenceDataService: ReferenceDataService,
    router: Router
  ) {
    super();

  }

  ngOnInit(): void {
    this.landholderDetails = this.vwds.getLandholderDetailsModel();
    this.landholderConfirmationDetails = this.vwds.getLandholderConfirmationModel();
    this.notifierDetails = this.vwds.getNotifierDetailsModel();
    this.notificationAreaDetails = this.vwds.getNotificationArea();
    this.lotOnPlanDetails = this.vwds.getLotPlanModel();

    this.categoryDetails = this.vwds.getCategories();

    if (this.categoryDetails !== null) {
      this.categoryDisplayValue = this.categoryDetails.map((category: ICategoryPurposeDataModel) => category.categoryCode).join(', ');
    }

    this.purposeDetails = this.vwds.getPurpose();
    this.activitiesDetails = this.vwds.getActivities();

    this.regionalEcosystems = this.vwds.getRegionalEcosystems();

    //This excludes the manually added REs from the list to be displayed (the manual REs are displayed in a separate list)
    this.regionalEcosystemsToDisplay = this.regionalEcosystems?.regionalEcosystems
      ?.filter(re => !this.regionalEcosystems?.regionalEcosystemsAddedFromSearchResults
        .some(res => res.categoryPurposeId == re.categoryPurposeId && res.regionalEcosystem == re.regionalEcosystem))
      .sort((a, b) => a.regionalEcosystem != null && b.regionalEcosystem != null ? a.regionalEcosystem.localeCompare(b.regionalEcosystem) : 0);
    this.regionalEcosystemStatuses = this.vwds.getRegionalEcosystemStatuses();

    this.isCoastalResponse = this.vwds.getIsCoastalResponse();
    this.acknowledgementsDetails = this.vwds.getAcknowledgementsData();
    this.acknowledgements = [];

    if (this.acknowledgementsDetails != null) {

      this.acknowledgements = this.acknowledgementsDetails.questions
        .map(question => question.description);

      if (this.acknowledgementsDetails.clearingInAForestArea != null && !!this.acknowledgementsDetails.clearingInAForestArea.message
        && !!this.acknowledgementsDetails.clearingInAForestArea.response) {
        this.acknowledgements.push(this.acknowledgementsDetails.clearingInAForestArea.message);
      }

      if (this.acknowledgementsDetails.stateCommercialTimberRights != null && !!this.acknowledgementsDetails.stateCommercialTimberRights.message
        && !!this.acknowledgementsDetails.stateCommercialTimberRights.response) {
        this.acknowledgements.push(this.acknowledgementsDetails.stateCommercialTimberRights.message);
      }

      if (this.acknowledgementsDetails.koalaHabitatProtection != null && !!this.acknowledgementsDetails.koalaHabitatProtection.message
        && !!this.acknowledgementsDetails.koalaHabitatProtection.response) {
        this.acknowledgements.push(this.acknowledgementsDetails.koalaHabitatProtection.message);
      }

      if(this.acknowledgementsDetails.wetTropicsQldWorldHeritageArea != null && !!this.acknowledgementsDetails.wetTropicsQldWorldHeritageArea.message
        && !!this.acknowledgementsDetails.wetTropicsQldWorldHeritageArea.response) {
          this.acknowledgements.push(this.acknowledgementsDetails.wetTropicsQldWorldHeritageArea.message);
        }
    }

    this.reasons = this.vwds.getReason();
    this.reasonText = this.getReasonTexts();
    this.channelDimensionsDetails = this.vwds.getChannelDimensions();
    this.exchangeAreaData = this.vwds.getExchangeArea();
    this.referenceDataService.getStates$().subscribe((data: IReferenceDataModel[]) => { this.states = data; });

  }

  removeDuplicates(categoryPurposeActivities: ICategoryPurposeActivityDataModel[]): ICategoryPurposeActivityDataModel[] {
    const flags: any = {};
    const output: ICategoryPurposeActivityDataModel[] = [];
    const arrayLength = categoryPurposeActivities.length;
    let index;

    for (index = 0; index < arrayLength; index++) {

      if (flags[categoryPurposeActivities[index].activityCode]) {
        continue;
      }

      flags[categoryPurposeActivities[index].activityCode] = true;

      output.push(categoryPurposeActivities[index]);
    }

    return output;
  }

  downloadMapFile(requestedFileName: string): void {
    const sessionId = this.sessionService.getSessionIdOrError();
    const url = `${environment.apiUrl}/session/download-areamap-file-preview?sessionKey=${encodeURIComponent(sessionId)}&requestedFileName=${encodeURIComponent(requestedFileName)}`;
    window.open(url, '_blank', '');
  }

  downloadEvidenceOfConsentFile(requestedFileName: string): void {
    const sessionId = this.sessionService.getSessionIdOrError();
    const url = `${environment.apiUrl}/session/download-evidenceofconsent-file-preview?sessionKey=${encodeURIComponent(sessionId)}&requestedFileName=${encodeURIComponent(requestedFileName)}`;
    window.open(url, '_blank', '');
  }

  getState(stateId: number): string {
    if (this.states !== null) {
      const state = this.states.find(state => state.id == stateId);
      if (state !== undefined) {
        return state.name ? state.name : '';
      }
    }

    return '';
  }

  getTextFromCode(code: string): string {
    const questionOptions = Object.values(QuestionReasonEnum);
    const questionCodes = Object.keys(QuestionReasonEnum).map(c => c[0].toUpperCase() + c.substring(1));
    const index = questionCodes.findIndex(c => c == code);
    return questionOptions[index];
  }

  getReasonTexts() : string[] | undefined{
    if(!this.reasons || this.reasons.length < 1){
      return undefined;
    }

    let result: string[] = [];

    if(this.featureFlag(FeatureFlags.NFP2019)) {
      for (let reason of this.reasons!) {
        let description = this.getTextFromCode(reason.code);

        if (reason.text != undefined && reason.text != '') {
          result.push(`${description}: ${reason.text}`);
        } else {
          result.push(description);
        }
      }
    }else{
      //If we are not using NFP feature this value is a free text field only so just return the first record.
      let reason = this.reasons[0];
      result.push(reason.text ?? '');
    }

    return result;
  }

  override next() {
    super.next();
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }

}
